// "Variants you explored" gallery — 🖼 topbar button.
//
// Lists the AI-generated variants this buyer has accumulated (localStorage +
// the cross-device Blob pulled in on session restore). Each thumbnail jumps to
// the room and applies the variant. This is the durable way to get back to a
// generated look — especially since the inline variant chips for generated
// variants can be hidden for client presentation (see variantPanel.ts).
//
// Reads generatedVariantStore.listGenerated(); groups by room; renders a simple
// responsive thumbnail grid into the shared ModalRoot.

import type { ModalRoot } from './modal';
import type { Room } from '../app/types';
import { listGenerated } from '../app/generatedVariantStore';

export interface VariantsGalleryDeps {
  modal: ModalRoot;
  getRooms: () => Room[];
  /** Navigate to the room and apply the variant (gallery closes after). */
  applyVariant: (roomId: string, variantId: string) => void;
}

export class VariantsGallery {
  private readonly deps: VariantsGalleryDeps;
  private button: HTMLButtonElement | null = null;

  constructor(deps: VariantsGalleryDeps) {
    this.deps = deps;
  }

  install(): void {
    this.button = document.getElementById('gallery-button') as HTMLButtonElement | null;
    if (this.button) this.button.addEventListener('click', () => this.open());
  }

  open(): void {
    const entries = listGenerated();
    const roomName = new Map(this.deps.getRooms().map((r) => [r.id, r.name]));

    const body = document.createElement('div');
    body.className = 'variants-gallery';

    if (entries.length === 0) {
      const empty = document.createElement('p');
      empty.style.cssText = 'opacity:0.75;line-height:1.5;margin:0.5rem 0;';
      empty.textContent =
        'Você ainda não gerou variantes. Use o botão ✨ para criar uma nova versão do ambiente — ela aparece aqui e te acompanha quando você salva a sessão.';
      body.appendChild(empty);
    } else {
      // Newest first.
      const sorted = [...entries].sort((a, b) => b.savedAt.localeCompare(a.savedAt));
      const grid = document.createElement('div');
      grid.style.cssText =
        'display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px;margin-top:0.5rem;';
      for (const entry of sorted) {
        grid.appendChild(this.renderCard(entry.roomId, entry.variant.id, entry.variant.name, entry.variant.panoramaUrl, roomName.get(entry.roomId) ?? entry.roomId));
      }
      body.appendChild(grid);
    }

    this.deps.modal.open({
      id: 'variants-gallery',
      title: `Variantes que você explorou${entries.length ? ` (${entries.length})` : ''}`,
      bodyNode: body,
    });
  }

  private renderCard(
    roomId: string,
    variantId: string,
    variantName: string,
    panoramaUrl: string,
    roomLabel: string,
  ): HTMLElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'variants-gallery__card';
    card.style.cssText =
      'display:flex;flex-direction:column;gap:6px;padding:0;border:1px solid rgba(255,255,255,0.15);' +
      'border-radius:10px;overflow:hidden;background:rgba(255,255,255,0.04);cursor:pointer;text-align:left;color:inherit;';
    card.setAttribute('aria-label', `${roomLabel} — ${variantName}`);

    const thumb = document.createElement('img');
    thumb.src = panoramaUrl;
    thumb.alt = `${roomLabel} — ${variantName}`;
    thumb.loading = 'lazy';
    thumb.style.cssText = 'width:100%;aspect-ratio:2/1;object-fit:cover;display:block;background:#1a2238;';

    const caption = document.createElement('div');
    caption.style.cssText = 'padding:6px 8px;';
    const v = document.createElement('div');
    v.textContent = variantName;
    v.style.cssText = 'font-size:0.82rem;font-weight:600;line-height:1.2;';
    const r = document.createElement('div');
    r.textContent = roomLabel;
    r.style.cssText = 'font-size:0.72rem;opacity:0.7;margin-top:2px;';
    caption.appendChild(v);
    caption.appendChild(r);

    card.appendChild(thumb);
    card.appendChild(caption);
    card.addEventListener('click', () => {
      this.deps.applyVariant(roomId, variantId);
      this.deps.modal.close();
    });
    return card;
  }
}
